// src/App.js
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { PostgresUserProvider, usePostgresUser } from './components/utils/PostgresUserContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClipLoader } from 'react-spinners';
import { attributionTracking } from './services/attributionTracking';

// EAGER: the public routes react-snap prerenders to static HTML. They must
// hydrate without waiting on a lazy chunk (React.lazy doesn't hydrate
// prerendered markup cleanly), so they stay in the main bundle. Plus the 404.
import OniKanjiLandingPage from './pages/onikanjiLandingPage';
import OniKanjiPricing from './pages/onikanjiPricing';
import AboutUsPage from './pages/aboutUs';
import DeleteAccountPage from './pages/deleteAccountPage';
import OnikanjiError from './pages/onikanjiError';
// Public kanji reference layer (SEO Phase 2a) — prerendered, so also eager.
import AllKanji from './pages/allKanji';
import KanjiLevels from './pages/kanjiLevels';
import JlptKanjiList from './pages/jlptKanjiList';
import OutputBattlePage from './pages/outputBattlePage';
import LeaderboardsPage from './pages/leaderboardsPage';
import { isAndroidApp } from './services/appContext';
import AppMembershipNotice from './components/AppMembershipNotice';
// Local mock stack only. The build replaces the env check with a constant, so
// production bundles drop the import entirely.
const DevMockToolbar = process.env.REACT_APP_MOCK_MODE === '1'
  ? React.lazy(() => import('./components/DevMockToolbar'))
  : null;

// LAZY: every other route is reached via the SPA shell (200.html — no
// prerendered markup to hydrate), so code-split it out of the initial bundle.
// This is what pulls Stripe (buyCoinsDisplay), recharts (stats), the readers,
// and the quiz/lesson engines off of every first paint and onto their own
// routes. Each must be a default export.
const OniKanjiLogin = lazy(() => import('./pages/onikanjiLogin'));
const OniKanjiSignUp = lazy(() => import('./pages/onikanjiSignUp'));
const KanjiHomePage = lazy(() => import('./pages/kanjiHomePage'));
const KanjiStatus = lazy(() => import('./pages/kanjiStatus'));
const KanjiDetail = lazy(() => import('./pages/kanjiDetails'));
const KanjiLevelUpPage = lazy(() => import('./pages/kanjiLevelUpPage'));
const TotalStats = lazy(() => import('./pages/kanjiTotalStats'));
const MyProfilePage = lazy(() => import('./pages/kanjiMyProfilePage'));
const ExtraStudySelectionScreen = lazy(() => import('./extraStudy/extraStudySelectionScreen'));
const ExtraStudyQuizDisplay = lazy(() => import('./extraStudy/extraStudyQuizDisplay'));
const ReaderDisplay = lazy(() => import('./components/readerEngine/readerDisplay'));
const AdminDashboard = lazy(() => import('./adminDashboard/AdminDashboard'));
const MangaReaderDisplay = lazy(() => import('./mangaReaderEngine/mangaReaderDisplay'));
const BuyCoinsDisplay = lazy(() => import('./coinEngine/buyCoinsDisplay'));
const PurchaseSuccess = lazy(() => import('./coinEngine/purchaseSuccess'));
const PurchaseCancel = lazy(() => import('./coinEngine/purchaseCancel'));
const KingsGambitQuizEngine = lazy(() => import('./kingsGambitQuizEngine/KingsGambitQuizEngine'));
const QueensGambitLessonEngine = lazy(() => import('./newLessonEngine/QueensGambitLessonEngine'));
const FrenchDefenseContentDetail = lazy(() => import('./frenchDefenseImmersionEngine/pages/FrenchDefenseContentDetail'));
const MyLibrary = lazy(() => import('./frenchDefenseImmersionEngine/pages/MyLibrary'));
const MyFavorites = lazy(() => import('./frenchDefenseImmersionEngine/pages/MyFavorites'));
const MyStudyList = lazy(() => import('./frenchDefenseImmersionEngine/pages/MyStudyList'));
const UserUpload = lazy(() => import('./frenchDefenseImmersionEngine/pages/UserUpload'));
const UserUploadReaderDisplay = lazy(() => import('./mangaReaderEngine/UserUploadReaderDisplay'));
const AnimeWatchPage = lazy(() => import('./animeWatch/AnimeWatchPage'));
const AnimeUploadPage = lazy(() => import('./animeWatch/AnimeUploadPage'));
const KanaReviewPage = lazy(() => import('./pages/kanaReviewPage'));
const ForumPage = lazy(() => import('./pages/forumPage'));

const queryClient = new QueryClient();

// Full-screen fallback shown while a lazy route chunk loads.
const PageLoader = () => (
  <div className="min-h-screen flex justify-center items-center bg-mainBackground">
    <ClipLoader size={50} color="#014156" />
  </div>
);

// Onboarding lives on the dashboard now (2026-10): new learners land on /home,
// whose welcome card starts the gentle first lesson or the level picker. The
// old /onboarding screens are no longer routed; old links go to /home.

// Android apps report themselves by package name (android-app://com.reddit.frontpage).
const APP_REFERRERS = {
  'com.reddit.frontpage': 'reddit.com',
  'com.google.android.googlequicksearchbox': 'google.com',
  'com.google.android.gm': 'gmail',
  'com.discord': 'discord.com',
  'com.Slack': 'slack.com',
};
const SEARCH_ENGINES = /(^|\.)(google|bing|duckduckgo|ecosia|yahoo|yandex|baidu|brave|startpage)\./;

// The external site this browser arrived from, e.g. "reddit.com", but only for
// the first landing of a visit that isn't already attributed.
function organicReferrer() {
  try {
    if (localStorage.getItem('attribution_visit_id')) return null;
    if (!document.referrer) return null;
    const raw = new URL(document.referrer).hostname.replace(/^(www|old|m|l|out)\./, '');
    const host = APP_REFERRERS[raw] || raw;
    if (!host || /(^|\.)onikanji\.com$/.test(host) || /(^|\.)stripe\.com$/.test(host) || host === 'localhost') return null;
    return host;
  } catch (e) {
    return null;
  }
}

// Old /immersion-hub links (still shared on Reddit) land logged-out visitors on the
// landing page instead of the library's login wall; members still get their library.
const ImmersionHubRedirect = () => {
  const { user, loading } = usePostgresUser();
  const { search } = useLocation();
  if (loading) return <PageLoader />;
  return <Navigate to={user ? '/immersion-hub/my-library' : `/${search}`} replace />;
};

// Create a component to wrap Routes with page tracking
const AppRoutes = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  React.useEffect(() => {
    // Get referral parameters from URL
    const refFromUrl = searchParams.get('ref');
    const utmSourceFromUrl = searchParams.get('utm_source');
    const campaignFromUrl = searchParams.get('campaign');

    // Google Ads click identifiers. Auto-tagging appends gclid (gbraid/wbraid
    // are the iOS/privacy-safe variants). These arrive WITHOUT ref/utm_source,
    // so the old `ref && utm_source` gate silently dropped every paid click —
    // which is exactly why we were flying blind on ad performance. Treat any
    // click id as an attributed paid visit. The keyword/campaign only arrive if
    // a ValueTrack final-URL suffix is configured in Google Ads
    // (utm_term={keyword}, utm_campaign={campaignid}, utm_medium=cpc).
    const gclid = searchParams.get('gclid');
    const gbraid = searchParams.get('gbraid');
    const wbraid = searchParams.get('wbraid');
    const hasPaidClickId = !!(gclid || gbraid || wbraid);

    if ((refFromUrl && utmSourceFromUrl) || hasPaidClickId) {
      // Track this as a new attributed visit
      const referralData = {
        ref: refFromUrl || (hasPaidClickId ? 'google-ads' : null),
        utm_source: utmSourceFromUrl || (hasPaidClickId ? 'google' : null),
        utm_medium: searchParams.get('utm_medium') || (hasPaidClickId ? 'cpc' : null),
        utm_term: searchParams.get('utm_term'), // the matched keyword (ValueTrack)
        campaign: campaignFromUrl || searchParams.get('utm_campaign'),
        gclid,
        gbraid,
        wbraid,
        landingPage: location.pathname + location.search
      };

      attributionTracking.trackVisit(referralData);
    } else if (organicReferrer()) {
      // Untagged arrival from another site (Reddit, Google, ChatGPT...). Without
      // this, only ?ref/utm/gclid links created a visit, so almost every signup
      // showed up as "direct / unknown". Only on a fresh landing, and never over
      // an existing (tagged or paid) attribution.
      const host = organicReferrer();
      attributionTracking.trackVisit({
        ref: 'organic',
        utm_source: host,
        utm_medium: SEARCH_ENGINES.test(host) ? 'organic' : 'referral',
        utm_term: null,
        campaign: null,
        landingPage: location.pathname + location.search,
      });
    } else {
      // Check if this is a subsequent page view for an attributed session
      attributionTracking.trackPageView();
    }
    
    const eventParams = {
      page_path: location.pathname + location.search,
      page_title: document.title,
    };

    // Add current referral data to analytics if available in storage
    const storedAttributionData = localStorage.getItem('attribution_data');
    if (storedAttributionData) {
      try {
        const parsedData = JSON.parse(storedAttributionData);
        if (parsedData.expiresAt && parsedData.expiresAt > Date.now()) {
          if (parsedData.ref) eventParams.ref = parsedData.ref;
          if (parsedData.utm_source) eventParams.utm_source = parsedData.utm_source;
          if (parsedData.campaign) eventParams.campaign = parsedData.campaign;
        }
      } catch (error) {
        // Ignore parsing errors
      }
    }
    
    // TODO: Replace with backend analytics if needed
    // logEvent was removed (Firebase analytics removal)

  }, [location, searchParams]);

  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      <Route path='/' element={<OniKanjiLandingPage />} />
      {/* Inside the Android app nothing is sold until Play Billing exists (Play rules). */}
      <Route path='/pricing' element={isAndroidApp() ? <AppMembershipNotice /> : <OniKanjiPricing />} />
      <Route path='/login' element={<OniKanjiLogin />} />
      <Route path='/verify' element={<OniKanjiLogin />} />
      <Route path='/signup' element={<OniKanjiSignUp />} />
      <Route path="/home" element={<KanjiHomePage />} />
      <Route path='/onboarding' element={<Navigate to='/home' replace />} />
      <Route path='/kanji' element={<AllKanji />} />
      <Route path='/kanji-status' element={<KanjiStatus />} />
      <Route path='/levels/:level' element={<KanjiLevels />} />
      <Route path='/jlpt/:level' element={<JlptKanjiList />} />
      <Route path='/kanji/:kanji' element={<KanjiDetail />} />
      <Route path='/new-lesson/' element={<QueensGambitLessonEngine />} />
      <Route path='/reviews/' element={<KingsGambitQuizEngine />} />
      <Route path='/level-up' element={<KanjiLevelUpPage />} />
      <Route path='/my-stats' element={<TotalStats />} />
      <Route path='/my-profile' element={<MyProfilePage />} />
      <Route path='/extra-study' element={<ExtraStudySelectionScreen />} />
      <Route path='/extra-study/custom-quiz' element={<ExtraStudyQuizDisplay />} />
      {/* The Immersion Hub catalog was retired in Sept 2026; old links land in the library (or the landing page if logged out). */}
      <Route path='/immersion-hub' element={<ImmersionHubRedirect />} />
      <Route path='/about-us' element={<AboutUsPage />} />
      <Route path='/delete-account' element={<DeleteAccountPage />} />
      <Route path='/admin' element={<AdminDashboard />} />
      <Route path='/get-coins' element={isAndroidApp() ? <AppMembershipNotice kind="coins" /> : <BuyCoinsDisplay />} />
      <Route path='/purchase-success' element={<PurchaseSuccess />} />
      <Route path='/purchase-cancel' element={<PurchaseCancel />} />
      <Route path='/manga-reader/:title/chapter/:chapter/page/:page' element={<MangaReaderDisplay />} />
      <Route path='/reader/:docId/:position' element={<ReaderDisplay />} />
      <Route path='/immersion-hub/my-library' element={<MyLibrary />} />
      <Route path='/immersion-hub/my-favorites' element={<MyFavorites />} />
      <Route path='/immersion-hub/my-study-list' element={<MyStudyList />} />
      <Route path='/immersion-hub/upload' element={<UserUpload />} />
      <Route path='/user-upload-reader/:uploadId/page/:page' element={<UserUploadReaderDisplay />} />
      <Route path='/anime-watch/upload' element={<AnimeUploadPage />} />
      <Route path='/anime-watch/:id' element={<AnimeWatchPage />} />
      <Route path='/immersion-hub/:contentId' element={<FrenchDefenseContentDetail />} />
      <Route path='/kana-review' element={<KanaReviewPage />} />
      <Route path='/forum' element={<ForumPage />} />
      <Route path='/output-battle' element={<OutputBattlePage />} />
      <Route path='/leaderboards' element={<LeaderboardsPage />} />
      <Route path='*' element={<OnikanjiError />} />
    </Routes>
    </Suspense>
  );
};

function App() {
  React.useEffect(() => {
    // TODO: Replace with backend analytics if needed
  }, []);

  return (
    <div className='App'>
        <PostgresUserProvider>
          <QueryClientProvider client={queryClient}>
            <Router>
                <AppRoutes />
              {DevMockToolbar && (
                <React.Suspense fallback={null}>
                  <DevMockToolbar />
                </React.Suspense>
              )}
            </Router>
          </QueryClientProvider>
        </PostgresUserProvider>
    </div>
  );
}

export default App;
